import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getLocalStorageAuthentication } from '../../../lib/auth';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import {
  createFileForUserSucceeded,
  fileDeletedSucceeded,
  fileIsEndProcessed,
  fileIsStartProcessed,
  getCompanyLogoFileSucceeded
} from '../file/filesSlice';
import {
  BASE_END_POINT_FILE,
  FILE_END_POINT,
  UPLOAD_FILE_END_POINT,
  GET_USER_FILE_END_POINT
} from '../../../config';

export const fileApi = createApi({
  reducerPath: 'fileApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_END_POINT_FILE}`
  }),
  endpoints: (build) => ({
    findFileByUserId: build.query({
      query: ({ userId, isPublic = false }) => {
        const userIdEncoded = encodeURIComponent(userId);
        const headers = {};
        
        // Only add authorization header if not a public request
        if (!isPublic) {
          const localAuth = getLocalStorageAuthentication();
          if (localAuth && localAuth.token) {
            headers.Authorization = `Bearer ${localAuth.token}`;
          }
        }
        
        return {
          url: `${FILE_END_POINT}${GET_USER_FILE_END_POINT}/${userIdEncoded}`,
          method: 'GET',
          headers
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          console.log('✅ [File API] File fetched successfully:', data);
          
          // Only dispatch to Redux if NOT a public request
          // Public requests use data directly from the hook
          if (!arg.isPublic) {
          // Dispatch file to Redux store
          const file = Array.isArray(data?.data) && data.data.length > 0
            ? data.data[0]
            : data?.data;
          if (file && file.id) {
            console.log('🖼️ [File API] Dispatching file to Redux');
            dispatch(getCompanyLogoFileSucceeded({ file }));
            }
          }
        } catch (error) {
          console.error('❌ [File API] Failed to fetch file:', error);
        }
      }
    }),
    uploadFile: build.mutation({
      query: ({ file }) => {
        const localAuth = getLocalStorageAuthentication();
        const formData = new FormData();
        formData.append('file', file);
        formData.append('userId', localAuth.userId);
        return {
          url: `${BASE_END_POINT_FILE}${FILE_END_POINT}${UPLOAD_FILE_END_POINT}`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: formData
        };
      },
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'File not created, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          dispatch(
            messageUpdated({
              text: 'Uploading file...',
              severity: Severity.INFO
            })
          );
          dispatch(fileIsStartProcessed());
          const { data } = await queryFulfilled;
          dispatch(fileIsEndProcessed());
          const file = data?.data;
          const isFileCreated = file && file.id;
          if (isFileCreated) {
            dispatch(
              createFileForUserSucceeded({
                file
              })
            );
            dispatch(
              messageUpdated({
                text: 'File created successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch (error) {
          dispatch(fileIsEndProcessed());
          errorMessage();
        }
      }
    }),
    deleteFile: build.mutation({
      query: ({ fileId }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${BASE_END_POINT_FILE}${FILE_END_POINT}/${encodeURIComponent(
            fileId
          )}`,
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          }
        };
      },
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = () => {
          dispatch(
            messageUpdated({
              text: 'File not deleted, please try again',
              severity: Severity.ERROR
            })
          );
        };
        try {
          dispatch(
            messageUpdated({
              text: 'Deleting file...',
              severity: Severity.INFO
            })
          );
          dispatch(fileIsStartProcessed());
          const { data } = await queryFulfilled;
          dispatch(fileIsEndProcessed());
          const file = data?.data;
          const isExistingFile = file && file.id;
          if (!isExistingFile) {
            dispatch(fileDeletedSucceeded());
            dispatch(
              messageUpdated({
                text: 'File deleted successfully',
                severity: Severity.SUCCESS
              })
            );
          } else {
            errorMessage();
          }
        } catch (error) {
          dispatch(fileIsEndProcessed());
          errorMessage();
        }
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { 
  useFindFileByUserIdQuery,
  useUploadFileMutation, 
  useDeleteFileMutation 
} = fileApi;
